/** @jsxImportSource @compiled/react */
import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { JiraIcon } from '@atlaskit/logo-next';

import { useIsSoftwareJiraPath } from '@/components/user-context/helpers/cohorts';
import { getLocalStorage, setLocalStorage } from '@/utils/localstorage';
import { useDeviceInfo } from '@/utils/use-device-info';

import { SmartBanner } from './smart-banner';

const STORAGE_KEY = 'SmartBanner_dismiss-jira';
const DISMISS_EXPIRY = 24 * 60 * 60;

const IOS_APP_STORE_URL = 'https://apps.apple.com/app/jira-cloud/id1006972087';
const APPLE_APP_ID = '1006972087';

// Android Intent URL with built-in Play Store fallback
const ANDROID_PLAY_STORE_URL =
	'https://play.google.com/store/apps/details?id=com.atlassian.android.jira.core';
const ANDROID_INTENT_URL = `intent://home#Intent;scheme=jira;package=com.atlassian.android.jira.core;S.browser_fallback_url=${encodeURIComponent(
	ANDROID_PLAY_STORE_URL,
)};end`;

export interface JiraSmartBannerProps {
	className?: string;
}

/**
 * SmartBanner - iOS/Android smart app banner for Jira
 *
 * This component:
 * - Only renders on iOS and Android devices
 * - Only renders on /software/jira and /software/jira/* paths
 * - Persists dismiss state in localStorage for 24 hours
 * - Attempts to open the Jira app via deep link, falls back to app store
 * - iOS Safari renders Apple's native Smart App Banner via an injected meta tag,
 *   gated on the same useIsSoftwareJiraPath condition as the custom banner
 */
export const JiraSmartBanner = ({ className }: JiraSmartBannerProps) => {
	const { isMobile, isAndroid, isMobileSafari } = useDeviceInfo();
	const [isDismissed, setIsDismissed] = useState(true);
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isSoftwareJiraPath = useIsSoftwareJiraPath();

	// iOS Safari does not render our custom banner; instead it uses Apple's
	// native Smart App Banner, which is driven by an `apple-itunes-app` meta tag.
	// Only inject it once useIsSoftwareJiraPath confirms the user is on a Jira
	// software path with a Jira instance — the same gate as the custom banner.
	useEffect(() => {
		if (isMobileSafari && isSoftwareJiraPath) {
			const meta = document.createElement('meta');
			meta.name = 'apple-itunes-app';
			meta.content = `app-id=${APPLE_APP_ID}, ct=wac_smart_banner`;
			document.head.appendChild(meta);

			return () => {
				if (meta.parentNode) {
					meta.parentNode.removeChild(meta);
				}
			};
		}
	}, [isMobileSafari, isSoftwareJiraPath]);

	useEffect(() => {
		const dismissValue = getLocalStorage(STORAGE_KEY, true);
		if (dismissValue !== '1') {
			setIsDismissed(false);
		}
	}, []);

	const handleClose = () => {
		setIsDismissed(true);
		setLocalStorage(STORAGE_KEY, '1', DISMISS_EXPIRY);
		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'smartBannerClose',
			eventType: 'ui',
			attributes: {
				productKey: 'jira',
				eventContainer: 'smartBanner',
			},
		}).fire();
	};

	const getDeepLinkUrl = useCallback(() => {
		if (isAndroid) {
			return ANDROID_INTENT_URL;
		}
		return IOS_APP_STORE_URL;
	}, [isAndroid]);

	if (!isMobile || isDismissed || isMobileSafari || !isSoftwareJiraPath) {
		return null;
	}

	return (
		<SmartBanner
			appIcon={<JiraIcon appearance="brand" size="medium" shouldUseNewLogoDesign />}
			title={intl.formatMessage({
				id: 'JiraSmartBanner.title',
				defaultMessage: 'Get the Jira app',
			})}
			subtitle={intl.formatMessage({
				id: 'JiraSmartBanner.subtitle',
				defaultMessage: 'Track tasks, projects, and work',
			})}
			buttonText={intl.formatMessage({
				id: 'JiraSmartBanner.buttonText',
				defaultMessage: 'OPEN',
			})}
			buttonHref={getDeepLinkUrl()}
			buttonAriaLabel={intl.formatMessage({
				id: 'JiraSmartBanner.buttonAriaLabel',
				defaultMessage: 'Open Jira app',
			})}
			onClose={handleClose}
			className={className}
			dataEventProps={{
				'data-product-key': 'jira',
				'data-event-container': 'smartBanner',
				'data-label': 'OPEN',
				'data-label-english': 'OPEN',
			}}
		/>
	);
};

export default JiraSmartBanner;
